import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/layout';
import { AuthProvider } from './contexts/AuthContext';
import { ErrorTrackingProvider } from './contexts/ErrorTrackingContext';
import { ToastProvider } from './components/ui';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SupportWidget } from './components/support/SupportWidget';
import { CookieConsent } from './components/CookieConsent';
import { useAuth } from './hooks';
import { useAdmin } from './hooks/useAdmin';

// Lazy-loaded pages
const Landing = lazy(() => import('./pages/Landing').then(m => ({ default: m.Landing })));
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Register = lazy(() => import('./pages/Register').then(m => ({ default: m.Register })));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const HireAgent = lazy(() => import('./pages/HireAgent').then(m => ({ default: m.HireAgent })));
const AgentDetail = lazy(() => import('./pages/AgentDetail').then(m => ({ default: m.AgentDetail })));
const AgentLive = lazy(() => import('./pages/AgentLive').then(m => ({ default: m.AgentLive })));
const AgentSkills = lazy(() => import('./pages/AgentSkills').then(m => ({ default: m.AgentSkills })));
const AgentAutomations = lazy(() => import('./pages/AgentAutomations').then(m => ({ default: m.AgentAutomations })));
const AgentChannels = lazy(() => import('./pages/AgentChannels').then(m => ({ default: m.AgentChannels })));
const AgentSettings = lazy(() => import('./pages/AgentSettings').then(m => ({ default: m.AgentSettings })));
const AgentRemote = lazy(() => import('./pages/AgentRemote').then(m => ({ default: m.AgentRemote })));
const AgentConversations = lazy(() => import('./pages/AgentConversations').then(m => ({ default: m.AgentConversations })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const Activity = lazy(() => import('./pages/Activity').then(m => ({ default: m.Activity })));
const TestStripe = lazy(() => import('./pages/TestStripe').then(m => ({ default: m.TestStripe })));
const Billing = lazy(() => import('./pages/Billing').then(m => ({ default: m.Billing })));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite').then(m => ({ default: m.AcceptInvite })));

// Lazy-loaded admin pages
const AdminLayout = lazy(() => import('./components/layout/AdminLayout').then(m => ({ default: m.AdminLayout })));
const AgentLayout = lazy(() => import('./components/layout/AgentLayout').then(m => ({ default: m.AgentLayout as React.ComponentType })));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers').then(m => ({ default: m.AdminUsers })));
const AdminUserDetail = lazy(() => import('./pages/admin/AdminUserDetail').then(m => ({ default: m.AdminUserDetail })));
const AdminAgents = lazy(() => import('./pages/admin/AdminAgents').then(m => ({ default: m.AdminAgents })));
const AdminAgentDetail = lazy(() => import('./pages/admin/AdminAgentDetail').then(m => ({ default: m.AdminAgentDetail })));
const AdminTickets = lazy(() => import('./pages/admin/AdminTickets').then(m => ({ default: m.AdminTickets })));
const AdminTicketDetail = lazy(() => import('./pages/admin/AdminTicketDetail').then(m => ({ default: m.AdminTicketDetail })));
const AdminSettingsPage = lazy(() => import('./pages/admin/AdminSettings').then(m => ({ default: m.AdminSettings })));
const AdminErrors = lazy(() => import('./pages/admin/AdminErrors').then(m => ({ default: m.AdminErrors })));

// Lazy-loaded legal pages
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService').then(m => ({ default: m.TermsOfService })));
const CookiePolicy = lazy(() => import('./pages/legal/CookiePolicy').then(m => ({ default: m.CookiePolicy })));

// Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-muted-foreground">Loading...</div>
  </div>
);

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// Admin route wrapper
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { isSuperAdmin, loading: adminLoading } = useAdmin();
  if (authLoading || adminLoading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isSuperAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes with layout */}
          <Route element={<Layout />}>
            <Route path="/" element={<Landing />} />
          </Route>

          {/* Auth routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />

          {/* Dashboard routes */}
          <Route element={<Layout showFooter={false} />}>
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/dashboard/agent/:id" element={<ProtectedRoute><AgentLayout /></ProtectedRoute>}>
              <Route index element={<AgentDetail />} />
              <Route path="live" element={<AgentLive />} />
              <Route path="skills" element={<AgentSkills />} />
              <Route path="automations" element={<AgentAutomations />} />
              <Route path="channels" element={<AgentChannels />} />
              <Route path="conversations" element={<AgentConversations />} />
              <Route path="settings" element={<AgentSettings />} />
            </Route>
            <Route path="/dashboard/activity" element={<ProtectedRoute><Activity /></ProtectedRoute>} />
            <Route path="/dashboard/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
            <Route path="/dashboard/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          </Route>

          <Route path="/dashboard/new" element={<ProtectedRoute><HireAgent /></ProtectedRoute>} />
          <Route path="/dashboard/agent/:id/remote" element={<ProtectedRoute><AgentRemote /></ProtectedRoute>} />
          <Route path="/test-stripe" element={<ProtectedRoute><TestStripe /></ProtectedRoute>} />

          {/* Admin routes with sidebar layout */}
          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:userId" element={<AdminUserDetail />} />
            <Route path="agents" element={<AdminAgents />} />
            <Route path="agents/:agentId" element={<AdminAgentDetail />} />
            <Route path="tickets" element={<AdminTickets />} />
            <Route path="tickets/:ticketId" element={<AdminTicketDetail />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="errors" element={<AdminErrors />} />
          </Route>

          {/* Legal */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiePolicy />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <SupportWidget />
      <CookieConsent />
    </BrowserRouter>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ErrorTrackingProvider>
        <AuthProvider>
          <ToastProvider>
            <AppRoutes />
          </ToastProvider>
        </AuthProvider>
      </ErrorTrackingProvider>
    </ErrorBoundary>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-foreground mb-4">404</h1>
        <p className="text-muted-foreground mb-8">Page not found</p>
        <a href="/" className="text-accent hover:text-accent-hover">Go back home</a>
      </div>
    </div>
  );
}

export default App;